import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class IdbService {
  private dbName = 'koom_com';
  private storeName = 'db';
  private local_storage_prefixe = 'kc_';
  private readonly dbVersion = 2;

  // ── Connexion mise en cache : on n'ouvre la DB qu'une seule fois ──
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor() {
    // Déclenche l'ouverture (et la mise en cache) dès le démarrage
    this.openDB().catch(err => console.error('Erreur d’initialisation IndexedDB :', err));
  }

  private openDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'key' });
        }
      };

      request.onsuccess = (event: any) => {
        const db = event.target.result;
        // Si une autre connexion (autre onglet) demande une mise à niveau,
        // on ferme proprement pour éviter de bloquer la mise à niveau.
        db.onversionchange = () => {
          db.close();
          this.dbPromise = null;
        };
        resolve(db);
      };

      request.onerror = () => {
        this.dbPromise = null; // on autorise une nouvelle tentative plus tard
        reject(request.error);
      };

      request.onblocked = () => {
        console.warn('Ouverture IndexedDB bloquée par une autre connexion (autre onglet ?)');
      };
    });

    return this.dbPromise;
  }

  async get_from_indexedDB(key: string): Promise<any> {
    const prefixedKey = this.local_storage_prefixe + key;
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const getRequest = store.get(prefixedKey);
      getRequest.onsuccess = () => resolve(getRequest.result ? getRequest.result.value : null);
      getRequest.onerror = () => {
        console.error('Erreur de récupération depuis IndexedDB');
        reject(getRequest.error);
      };
    });
  }

  async save_on_indexedDB(key: string, value: any): Promise<void> {
    const prefixedKey = this.local_storage_prefixe + key;
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const putRequest = store.put({ key: prefixedKey, value });
      // On attend la fin de la TRANSACTION, pas juste de la requête,
      // pour être sûr que l'écriture est bien "commit" sur disque.
      transaction.oncomplete = () => resolve();
      putRequest.onerror = () => {
        console.error('Erreur lors de la sauvegarde dans IndexedDB');
        reject(putRequest.error);
      };
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error || new Error('Transaction avortée'));
    });
  }

  async delete_from_indexedDB(key: string): Promise<void> {
    const prefixedKey = this.local_storage_prefixe + key;
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const deleteRequest = store.delete(prefixedKey);
      transaction.oncomplete = () => resolve();
      deleteRequest.onerror = () => {
        console.error('Erreur lors de la suppression dans IndexedDB');
        reject(deleteRequest.error);
      };
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error || new Error('Transaction avortée'));
    });
  }
}